/**
 * Show/hide toggles for the diagram's stages.
 *
 * A view control, not a launch control — nothing here starts or stops anything.
 * `×` and solid means the stage is on screen; `+` and dashed means it is
 * available to add. The prefix carries that, so the chips read the same way
 * without relying on the border alone.
 */

import { Box, Button, useTheme } from '@mui/material';

import { flowToken } from './tokens';

export interface FlowModule {
  key: string;
  label: string;
}

interface FlowModuleChipsProps {
  modules: FlowModule[];
  /** Keys currently on screen. */
  enabled: string[];
  onToggle: (key: string) => void;
}

export default function FlowModuleChips({
  modules,
  enabled,
  onToggle,
}: FlowModuleChipsProps) {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {modules.map((module) => {
        const on = enabled.includes(module.key);

        return (
          <Button
            key={module.key}
            size="small"
            onClick={() => onToggle(module.key)}
            sx={{
              minWidth: 0,
              px: 1.75,
              py: 0.5,
              borderRadius: 100,
              textTransform: 'none',
              fontSize: 11,
              fontWeight: 500,
              border: `1px ${on ? 'solid' : 'dashed'} ${flowToken(theme, 'wash650')}`,
              color: flowToken(theme, on ? 'wash200' : 'wash500'),
              '&:hover': { backgroundColor: flowToken(theme, 'wash850') },
            }}
          >
            {on ? '× ' : '+ '}
            {module.label}
          </Button>
        );
      })}
    </Box>
  );
}
